import {
  fetcher,
  useApplicantAPI,
  usePollingBackoff,
  useRequest,
} from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import { ThemeContext } from "@certn/cascadia";
import {
  BasicButton,
  ButtonGroup,
  GhostButton,
  Illustration,
  Link,
  PrimaryButton,
  Space,
  Stack,
  Text,
  showToast,
} from "@certn/cascadia-react";
import { OneIdImage } from "@certn/cascadia-react/foundations/illustrations/OneIdImage";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { getPrivacyPolicyUrl, getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  BiometricConsentModal,
  useBiometricConsentStatus,
} from "./BiometricConsentModal";
import { ConfirmCloseTrustmaticModal } from "./ConfirmCloseOneIdIFrameModal";
import { DemoSkipModal } from "./DemoSkipModal";
import { OneIdIFrameModal } from "./OneIdIFrameModal";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import Constants from "../../constants";
import { OneIdLayoutContext } from "../../layouts/OneIdLayoutContext";
import { BiometricNoticeModal } from "./BiometricNoticeModal";
import { isOneId1SectionComplete } from "./isOneId1SectionComplete";
import { OneIDLoader } from "./OneIDLoader";

const SESSION_TERMINATED_BY_ERRORS = "SESSION_TERMINATED_BY_ERRORS";

type OneIdCheckStatusResponse =
  components["schemas"]["OneIDCheckStatusResponse"];

const DEMO_SKIP_SESSION_ID = "00000000-0000-0000-0000-000000000000";

type CreateSessionData = {
  src_url: string;
  one_id_session_id: string;
};

export type TrustmaticEventNotification = {
  hasCompleted: boolean;
  hasError: boolean;
  sessionId: string;
  error?: string;
};

export function OneIdControl(props: CascadiaControlProps) {
  const { t, i18n } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const region = getRegionKey();

  const { values } = useContext(CascadiaJsonFormContext);
  const { label, subLabel } = useContext(OneIdLayoutContext);

  const { theme } = useContext(ThemeContext);
  const language = i18n.language.split("-")[0];
  const privacyPolicyUrl = getPrivacyPolicyUrl(language);
  const contractId =
    language === "fr"
      ? props.uischema?.options?.french_consent_id
      : region === "au"
        ? props.uischema?.options?.australian_consent_id
        : props.uischema?.options?.english_consent_id;
  const request = useRequest();

  const {
    onlyFulfillWithDemoResponses,
    navigateToSection,
    prevSection,
    saveData,
  } = useApplication();

  const [isTrustmaticUrlLoading, setIsTrustmaticUrlLoading] = useState(false);
  const [trustmaticUrlError, setTrustmaticUrlError] = useState(false);
  const [trustmaticUrl, setTrustmaticUrl] = useState("");
  const [oneIdSessionId, setOneIdSessionId] = useState<string>(props.data);
  const [showIFrame, setShowIFrame] = useState(false);
  const [trustmaticResponse, setTrustmaticResponse] = useState<
    TrustmaticEventNotification | undefined
  >(undefined);
  const [openConfirmCloseTrustmaticModal, setOpenConfirmCloseTrustmaticModal] =
    useState(false);
  const [showDemoSkipModal, setShowDemoSkipModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentedThisSession, setConsentedThisSession] = useState(false);
  const [startPollingOneIdStatus, setStartPollingOneIdStatus] = useState(false);
  const [sessionTerminatedByErrors, setSessionTerminatedByErrors] =
    useState(false);

  const {
    applicantPortalLogTrustmaticResponseTimes,
    applicantPortalBiometricConsentModal,
  } = useFlags();
  const {
    hasConsentedToCurrentVersion,
    superdocEnabled,
    superdocEsignAvailable,
  } = useBiometricConsentStatus(contractId);
  const skipConsentModal = hasConsentedToCurrentVersion || consentedThisSession;

  const checkStatusRefreshInterval =
    usePollingBackoff<OneIdCheckStatusResponse>({
      // CANCELLED covers session termination. isOneId1SectionComplete stays
      // false for that path because the session-complete flag is not set.
      shouldStop: (data) =>
        isOneId1SectionComplete(data) || data?.check_status === "CANCELLED",
    });

  // Only schedule repeat polling once the applicant has actually started
  // verification. Before then the SWR config is `{}` so the endpoint is
  // fetched once (enough to reflect an already-complete check on revisit)
  // without entering the exponential-backoff poll loop — avoiding needless
  // load / WAF risk for applicants sitting in AWAITING_APPLICANT_SUBMISSION.
  // Gating the config object (not just the backoff predicate) is what lets
  // SWR react when `startPollingOneIdStatus` flips true.
  const checkStatusSwrConfig = useMemo(
    () =>
      startPollingOneIdStatus
        ? { refreshInterval: checkStatusRefreshInterval }
        : {},
    [startPollingOneIdStatus, checkStatusRefreshInterval]
  );

  const { data: oneIdCheckStatus, mutate: refreshOneIdCheckStatus } =
    useApplicantAPI(
      `/api/internal/applicant/case/${caseId}/one-id-check-status/`,
      {},
      checkStatusSwrConfig
    );

  // `sessionTerminatedByErrors` lives only in this mount. Refresh, back
  // navigation, or leaving during the loader drops it, and
  // isOneId1SectionComplete stays false for CANCELLED — which sent applicants
  // back to Start Verification on an already cancelled check. The persisted
  // status is the same outcome.
  const terminationSettled = oneIdCheckStatus?.check_status === "CANCELLED";
  const persistedTerminatedSessionId = useRef<string | undefined>(undefined);

  const isSectionComplete = useMemo(() => {
    if (isOneId1SectionComplete(oneIdCheckStatus)) {
      return true;
    }
    if (trustmaticResponse?.hasCompleted && !trustmaticResponse?.hasError) {
      return true;
    }
    if (Constants.enableDemoResponses && props.data === DEMO_SKIP_SESSION_ID) {
      return true;
    }
    return false;
  }, [oneIdCheckStatus, props.data, trustmaticResponse]);

  // Success writes one_id_session_id before Next. Termination sets hasError,
  // so that write is skipped, and packages that require the UUID then fail
  // validation. Recover it once the check is CANCELLED: the create call
  // returns the existing session for a terminal check, including after remount.
  const { data: storedSessionId, handleChange, path } = props;
  useEffect(() => {
    if (!terminationSettled || !oneIdSessionId) return;
    if (storedSessionId === oneIdSessionId) return;
    if (persistedTerminatedSessionId.current === oneIdSessionId) return;
    persistedTerminatedSessionId.current = oneIdSessionId;

    handleChange(path, oneIdSessionId);
    const persistSessionId = async () => {
      try {
        await saveData({ ...values, [path]: oneIdSessionId });
      } catch (e) {
        showToast(t("Something went wrong, try again"));
        Sentry.captureException(e);
      }
    };
    void persistSessionId();
  }, [
    handleChange,
    oneIdSessionId,
    path,
    saveData,
    storedSessionId,
    t,
    terminationSettled,
    values,
  ]);

  const signAgreement = async () => {
    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/sign-agreement/`,
        {
          external_id: contractId,
        }
      );
    } catch (e) {
      console.error("Error signing Biometric Consent", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      Sentry.captureException(e);
      return false;
    }

    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/biometric-consent/create-claim/`
      );
    } catch (e) {
      console.error("Error creating Biometric Consent claim", e);
      Sentry.captureException(e);
      return false;
    }
    return true;
  };

  const openVerification = async () => {
    setStartPollingOneIdStatus(true);
    // In SuperDoc mode the signature + biometric claim are already recorded by
    // the agreement-signature endpoint, so skip the PactSafe sign + create-claim.
    // Gate on `superdocEsignAvailable` (esign.url present), not the raw LD flag:
    // when the backend falls back to PactSafe (flag on, no esign.url) we still
    // need sign-agreement/create-claim. After a fresh SuperDoc signature the
    // biometric-consent SWR cache may still be stale-false, but every path that
    // reaches `openVerification` with esign available has already recorded consent.
    const agreementSigned = superdocEsignAvailable
      ? true
      : await signAgreement();
    if (agreementSigned) {
      setShowIFrame(true);
    }
    return agreementSigned;
  };

  const closeTrustmaticModal = () => {
    showIFrame && setOpenConfirmCloseTrustmaticModal(true);
  };

  const returnToTrustmaticModal = () => {
    setOpenConfirmCloseTrustmaticModal(false);
  };

  const confirmCloseTrustmaticModal = () => {
    if (showIFrame) {
      setOpenConfirmCloseTrustmaticModal(false);
      setShowIFrame(false);
    }
  };

  const handleCloseIFrame = async () => {
    try {
      await request(`/api/internal/applicant/case/${caseId}/one-id/end/`, {
        method: "POST",
        body: JSON.stringify({
          one_id_session_id: oneIdSessionId,
        }),
      });
    } catch (e) {
      console.error("Error creating log for One ID session end:", e);
    }
  };

  const getTrustmaticLink = useCallback(async () => {
    if (trustmaticUrl) return; // if we have a url don't fetch another one.
    if (onlyFulfillWithDemoResponses) return;

    setIsTrustmaticUrlLoading(true);
    setTrustmaticUrlError(false);
    try {
      const { src_url, one_id_session_id } = await request<CreateSessionData>(
        `/api/internal/applicant/case/${caseId}/one-id/create/`,
        {
          method: "POST",
          body: JSON.stringify({ color_scheme: theme }),
        }
      );
      setTrustmaticUrl(src_url);
      setOneIdSessionId(one_id_session_id);
    } catch (e) {
      console.error("Error starting verification:", e);
      setTrustmaticUrlError(true);
    } finally {
      setIsTrustmaticUrlLoading(false);
    }
  }, [caseId, request, theme, trustmaticUrl, onlyFulfillWithDemoResponses]);

  useEffect(() => {
    getTrustmaticLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enrollmentCompleted = useCallback(
    async (event: MessageEvent) => {
      const { data }: { data: TrustmaticEventNotification } = event;
      // Helps protect against other message events interfering
      if (data.sessionId) {
        setTrustmaticResponse(data);

        // FR-569: only mark the section complete once Trustmatic reports the
        // verification has finished. Persisting the session id on any message
        // (even before completion) previously let applicants submit with the
        // OneID check left stuck in AWAITING_APPLICANT_SUBMISSION.
        if (data.hasCompleted && !data.hasError) {
          props.handleChange(props.path, oneIdSessionId);

          try {
            await saveData({ ...values, [props.path]: oneIdSessionId });
            await refreshOneIdCheckStatus();
          } catch (e) {
            showToast(t("Something went wrong, try again"));
            Sentry.captureException(e);
          }
        }
      }
    },
    [oneIdSessionId, props, refreshOneIdCheckStatus, saveData, t, values]
  );

  useEffect(() => {
    if (showIFrame) {
      window.addEventListener("message", enrollmentCompleted, false);
    } else {
      window.removeEventListener("message", enrollmentCompleted, false);
    }
  }, [enrollmentCompleted, showIFrame]);

  useEffect(() => {
    const handleTrustmaticErrorEvent = (
      _trustmaticResponse: TrustmaticEventNotification
    ) => {
      console.error("Error starting verification:", _trustmaticResponse.error);
      showToast(t("Something went wrong, try again"), { type: "error" });
    };

    if (trustmaticResponse?.error === SESSION_TERMINATED_BY_ERRORS) {
      setShowIFrame(false);
      setSessionTerminatedByErrors(true);
      setStartPollingOneIdStatus(true);
      refreshOneIdCheckStatus();
      return;
    }

    if (trustmaticResponse?.hasCompleted) {
      setShowIFrame(false);
      if (applicantPortalLogTrustmaticResponseTimes) {
        handleCloseIFrame();
      }
    }

    if (trustmaticResponse?.hasError) {
      handleTrustmaticErrorEvent(trustmaticResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustmaticResponse, t]);

  if (sessionTerminatedByErrors && !terminationSettled) {
    return <OneIDLoader />;
  }

  return (
    <>
      <Stack gap="space-2">
        <div className={styles.heading}>
          <Text
            as="h1"
            style="form/page-title"
            testID="identity-verification-section-title"
          >
            {label}
          </Text>

          {subLabel && (
            <Text
              style="form/body-primary"
              color="text-weak"
              testID="identity-verification-section-subtitle"
            >
              {terminationSettled
                ? t(
                    "We failed to verify your identity using that method. Please continue to the next step."
                  )
                : props.data && !isSectionComplete
                  ? subLabel
                  : isSectionComplete
                    ? t(
                        "Your verification details have been submitted. Please continue to the next step."
                      )
                    : subLabel}
            </Text>
          )}
        </div>
        <div className={styles.image}>
          {terminationSettled ? (
            <Illustration name="oneid-fail" size={132} />
          ) : isSectionComplete ? (
            <Illustration name="oneid-success" size={132} />
          ) : (
            <OneIdImage />
          )}
        </div>
      </Stack>

      <OneIdIFrameModal
        show={showIFrame}
        onClose={closeTrustmaticModal}
        trustmaticUrl={trustmaticUrl}
        isError={trustmaticUrlError}
      />
      <ConfirmCloseTrustmaticModal
        show={openConfirmCloseTrustmaticModal}
        onClose={returnToTrustmaticModal}
        onExit={confirmCloseTrustmaticModal}
      />
      {!isSectionComplete &&
        !sessionTerminatedByErrors &&
        !terminationSettled && (
          <>
            <Text
              style="form/body-secondary"
              color="text-weak"
              testID="biometric-notice-statement"
            >
              {applicantPortalBiometricConsentModal ? (
                <Trans>
                  Identity verification requires capturing a photo of your
                  government ID and a selfie to confirm your identity. Certn
                  uses biometric verification to compare these images and
                  confirm your identity. Before starting, you'll be asked to
                  review and consent to the{" "}
                  <BiometricNoticeModal
                    linkName={t("Biometric Notice")}
                    modalHeader={t("Biometric Notice")}
                    contractId={contractId}
                  />
                  , which explains how Certn collects and uses biometric data.
                </Trans>
              ) : (
                <Trans>
                  By clicking "Start verification" below, you consent to the
                  processing of your biometric data for this purpose, as further
                  explained in our{" "}
                  <BiometricNoticeModal
                    linkName={t("Biometric Notice")}
                    modalHeader={t("Biometric Notice")}
                    contractId={contractId}
                  />
                  .
                </Trans>
              )}
            </Text>
            <Text
              style="form/body-secondary"
              color="text-weak"
              testID="privacy-policy-statement"
            >
              {applicantPortalBiometricConsentModal ? (
                <Trans>
                  You can also review Certn's{" "}
                  <Link
                    linkStyle="monochrome"
                    testID="privacy-policy-link"
                    href={privacyPolicyUrl}
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>{" "}
                  for more information about how personal information is
                  handled.
                </Trans>
              ) : (
                <Trans>
                  Please see our{" "}
                  <Link
                    linkStyle="monochrome"
                    testID="privacy-policy-link"
                    href={privacyPolicyUrl}
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>{" "}
                  for more information about Certn's privacy practices and your
                  privacy rights.
                </Trans>
              )}
            </Text>
          </>
        )}

      <div className={css(styles.actionBar)}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => {
                navigateToSection(prevSection);
              }}
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          {isSectionComplete || terminationSettled ? (
            <PrimaryButton testID="section-next-step-button" type="submit">
              {t("Next step")}
            </PrimaryButton>
          ) : (
            <>
              {Constants.enableDemoResponses && !isTrustmaticUrlLoading && (
                <BasicButton onPress={() => setShowDemoSkipModal(true)}>
                  {t("Demo Response")}
                </BasicButton>
              )}
              {!onlyFulfillWithDemoResponses && (
                <PrimaryButton
                  onPress={() => {
                    if (
                      skipConsentModal ||
                      (!applicantPortalBiometricConsentModal &&
                        !superdocEnabled)
                    ) {
                      openVerification();
                    } else {
                      setShowConsentModal(true);
                    }
                  }}
                  isDisabled={!oneIdSessionId}
                  isLoading={!oneIdSessionId}
                >
                  {t("Start Verification")}
                </PrimaryButton>
              )}
            </>
          )}
        </ButtonGroup>
      </div>

      {Constants.enableDemoResponses && !isTrustmaticUrlLoading && (
        <DemoSkipModal
          isOpen={showDemoSkipModal}
          onClose={() => setShowDemoSkipModal(false)}
          onSave={() => {
            props.handleChange(
              props.path,
              "00000000-0000-0000-0000-000000000000"
            );
            setShowDemoSkipModal(false);
          }}
        />
      )}
      <BiometricConsentModal
        isOpen={showConsentModal}
        contractId={contractId}
        onConsent={async () => {
          const success = await openVerification();
          if (success) {
            setConsentedThisSession(true);
            setShowConsentModal(false);
          }
        }}
        onCancel={() => setShowConsentModal(false)}
      />
    </>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
  image: css({
    display: "flex",
    justifyContent: "center",
  }),
};

OneIdControl.displayName = "OneIdControl";

export const OneIdControlRenderer = withCascadiaControlProps(OneIdControl);
